import theme from '@styles/theme';
import { TButtonLetterCase, TButtonSize, TButtonVariant, TButtonVariantColor, TButtonVariantBackgroundColor } from '@components/Button/typescript';
import reusables from '@styles/theme/reusables';
import buttonThemes from '@components/Button/themes';
const variantDispatcher = (variant: TButtonVariant, variantColor: TButtonVariantColor, variantBackgroundColor: TButtonVariantBackgroundColor | undefined, size: TButtonSize, active?: boolean) => {
  const {
    buttonHeights,
    variants
  } = buttonThemes;
  const getBackgroundColor = (_variantColor: string, defaultColor: string) => {
    if (active) {
      return theme.rawColors.lightGrey500;
    }
    if (variantBackgroundColor) {
      return _variantColor || defaultColor;
    }
    return defaultColor;
  };
  switch (variant) {
    case 'default':
    case 'outlined':
      {
        const {
          backgroundColor,
          color,
          borderColor,
          fill
        } = variants[variant];
        return {
          p: '0 24px',
          height: size ? buttonHeights[size] : buttonHeights.default,
          backgroundColor: getBackgroundColor(variantBackgroundColor ? variants.default.backgroundColor[variantBackgroundColor] : '', backgroundColor.default[variantColor]),
          borderColor: active ? theme.rawColors.lightGrey500 : borderColor.default[variantColor],
          color: color.default[variantColor],
          'a, a:visited': {
            color: color.default[variantColor]
          },
          '.icon svg path': {
            fill: fill.default[variantColor]
          },
          '&:hover': {
            backgroundColor: getBackgroundColor(variantBackgroundColor ? variants.default.backgroundColor[variantBackgroundColor] : '', backgroundColor.hover[variantColor]),
            borderColor: active ? theme.rawColors.lightGrey500 : borderColor.hover[variantColor],
            color: color.hover[variantColor],
            a: {
              color: color.hover[variantColor]
            },
            '.icon svg path': {
              fill: fill.hover[variantColor]
            }
          },
          '&:focus': {
            backgroundColor: getBackgroundColor(variantBackgroundColor ? variants.default.backgroundColor[variantBackgroundColor] : '', backgroundColor.focus[variantColor]),
            color: color.focus[variantColor],
            outlineOffset: '2px',
            a: {
              color: color.focus[variantColor]
            },
            '.icon svg path': {
              fill: fill.focus[variantColor]
            }
          },
          '&:disabled': {
            backgroundColor: variantBackgroundColor && variants.default.backgroundColor[variantBackgroundColor] || backgroundColor.disabled[variantColor],
            borderColor: borderColor.disabled[variantColor],
            color: color.disabled[variantColor],
            '.icon svg path': {
              fill: fill.disabled[variantColor]
            }
          }
        };
      }
    case 'iconOnly':
      {
        const {
          fill
        } = variants[variant];
        const {
          backgroundColor
        } = variants.default;
        return {
          backgroundColor: variantBackgroundColor && backgroundColor.default[variantBackgroundColor] || 'transparent',
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          '.icon svg path': {
            fill: fill.default[variantColor]
          },
          '&:hover': {
            '.icon svg path': {
              fill: fill.hover[variantColor]
            }
          },
          '&:disabled': {
            '.icon svg path': {
              fill: fill.disabled[variantColor]
            }
          }
        };
      }
    case 'link':
      {
        const {
          fill,
          color
        } = variants[variant];
        const {
          backgroundColor
        } = variants.default;
        return {
          backgroundColor: variantBackgroundColor && backgroundColor.default[variantBackgroundColor] || 'transparent',
          border: 'none',
          borderColor: 'none',
          color: color.default[variantColor],
          '.icon svg path': {
            fill: fill.default[variantColor]
          },
          '&:hover': {
            color: color.hover[variantColor],
            '.icon svg path': {
              fill: fill.hover[variantColor]
            }
          },
          '&:focus': {
            color: color.focus[variantColor],
            '.icon svg path': {
              fill: fill.focus[variantColor]
            }
          },
          '&:disabled': {
            color: color.disabled[variantColor],
            '.icon svg path': {
              fill: fill.disabled[variantColor]
            }
          }
        };
      }
    case 'link_underline':
      {
        const {
          fill,
          color
        } = variants[variant];
        const {
          backgroundColor
        } = variants.default;
        return {
          backgroundColor: variantBackgroundColor && backgroundColor.default[variantBackgroundColor] || 'transparent',
          border: 'none',
          textDecoration: 'underline',
          borderColor: 'none',
          color: color.default[variantColor],
          '.icon svg path': {
            fill: fill.default[variantColor]
          },
          '&:hover': {
            color: color.hover[variantColor],
            '.icon svg path': {
              fill: fill.hover[variantColor]
            }
          },
          '&:focus': {
            '.icon svg path': {
              fill: fill.focus[variantColor]
            }
          },
          '&:disabled': {
            color: color.disabled[variantColor],
            '.icon svg path': {
              fill: fill.disabled[variantColor]
            }
          }
        };
      }
    default:
      return {};
  }
};
export default {
  shared: (variant?: TButtonVariant, size?: TButtonSize, letterCase?: TButtonLetterCase, fullWidth?: boolean, disabled?: boolean, loading?: boolean) => {
    if (!size || !letterCase) return {};
    const {
      fontSizes
    } = buttonThemes;
    const isIconOnly = () => variant === 'iconOnly';
    const isUppercase = () => letterCase === 'uppercase';
    return {
      userSelect: 'none',
      borderWidth: isIconOnly() ? 'none' : '1px',
      borderStyle: isIconOnly() ? 'none' : 'solid',
      position: 'relative',
      display: 'inline-flex',
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: theme.fonts.uniform,
      fontWeight: theme.fontWeights.normal,
      textTransform: letterCase,
      letterSpacing: '0.05em',
      width: fullWidth ? '100%' : 'auto',
      fontSize: fontSizes[size][letterCase],
      cursor: disabled ? 'not-allowed !important' : 'pointer',
      pointerEvents: loading ? 'none' : 'auto',
      ...(isUppercase() ? {
        fontFamily: theme.fonts.uniformExtra
      } : {}),
      ...reusables.mainTransition('color, border-color, background-color', theme.transitions.animation.atomCurve, theme.transitions.time.fast)
    };
  },
  variant: variantDispatcher,
  loadingWrapper: (size?: TButtonSize) => {
    if (!size) return {};
    const scale = buttonThemes.buttonHeights[size] / 180;
    const offset = buttonThemes.buttonHeights[size] / 6;
    return {
      transform: `scale(${scale}) translate(-50%, calc(50% - ${offset}px))`,
      position: 'absolute',
      top: `calc(50% - ${offset}px)`,
      left: '50%'
    };
  },
  childrenWrapper: (loading?: boolean) => ({
    opacity: loading ? 0 : 1,
    display: 'inline-flex'
  })
};